// ============================================
// HOOK - Flow (obtener datos de Flow)
// ============================================

import { useQuery, useMutation } from '@tanstack/react-query';
import { flowRepository } from '../../infrastructure/http/flow.repository';
import type {
  CreateSubscriptionRequest,
  RegisterCreditCardRequest,
  CancelSubscriptionRequest,
} from '../../domain/entities';

/**
 * Hook para obtener un plan de suscripción de Flow
 */
export const usePlan = (planId: number, enabled = true) => {
  return useQuery({
    queryKey: ['flow', 'plan', planId],
    queryFn: () => flowRepository.getPlan(planId),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
};

/**
 * Hook para obtener información del cliente en Flow
 */
export const useFlowCustomer = (rutAlmacen: string, enabled = true) => {
  return useQuery({
    queryKey: ['flow', 'customer', rutAlmacen],
    queryFn: () => flowRepository.getCustomer(rutAlmacen),
    enabled: enabled && !!rutAlmacen,
    retry: false, // No reintentar si el cliente no existe (404)
    refetchOnMount: 'always', // Siempre refrescar al montar el componente
    staleTime: 0, // Los datos se consideran obsoletos inmediatamente
  });
};

/**
 * Hook para obtener el detalle completo de una suscripción
 */
export const useSubscriptionDetail = (subscriptionId: string | null, enabled = true) => {
  return useQuery({
    queryKey: ['flow', 'subscription', 'detail', subscriptionId],
    queryFn: () => flowRepository.getSubscriptionDetail(subscriptionId!),
    enabled: enabled && !!subscriptionId,
    refetchOnMount: 'always', // Siempre refrescar al montar el componente
    staleTime: 0, // Los datos se consideran obsoletos inmediatamente
  });
};

/**
 * Hook para obtener información de una factura
 */
export const useInvoice = (invoiceId: number | null, enabled = true) => {
  return useQuery({
    queryKey: ['flow', 'invoice', invoiceId],
    queryFn: () => flowRepository.getInvoice(invoiceId!),
    enabled: enabled && !!invoiceId,
    refetchOnMount: 'always', // Siempre refrescar al montar el componente
    staleTime: 0, // Los datos se consideran obsoletos inmediatamente
  });
};

/**
 * Hook para crear una suscripción (pago manual por correo)
 */
export const useCreateSubscription = () => {
  return useMutation({
    mutationFn: (request: CreateSubscriptionRequest) => 
      flowRepository.createSubscription(request),
  });
};

/**
 * Hook para registrar tarjeta de crédito (cargo automático)
 */
export const useRegisterCreditCard = () => {
  return useMutation({
    mutationFn: (request: RegisterCreditCardRequest) => 
      flowRepository.registerCreditCard(request),
  });
};

/**
 * Hook para cancelar una suscripción
 */
export const useCancelSubscription = () => {
  return useMutation({
    mutationFn: (request: CancelSubscriptionRequest) => 
      flowRepository.cancelSubscription(request),
  });
};

/**
 * Hook para verificar el estado de la suscripción en Flow
 */
export const useSubscriptionStatus = (rutAlmacen: string, enabled = true) => {
  return useQuery({
    queryKey: ['flow', 'subscription-status', rutAlmacen],
    queryFn: () => flowRepository.getSubscriptionStatus(rutAlmacen),
    enabled: enabled && !!rutAlmacen,
    retry: false,
  });
};

/**
 * Hook para buscar un cupón de descuento por código
 */
export const useBuscarCupon = () => {
  return useMutation({
    mutationFn: (codigo: string) => flowRepository.getCupon(codigo),
  });
};
